/**
 * Safe, app-owned entry point for Replit-hosted Umami events.
 *
 * The tracker is injected by Replit on supported published web artifacts.
 * Nothing in this module configures the tracker, and analytics failures are
 * deliberately isolated from the quote builder.
 */

export const BUILDER_ANALYTICS_EVENTS = [
  "quote_created",
  "quote_sent",
  "quote_exported",
] as const;

export type BuilderAnalyticsEvent = (typeof BUILDER_ANALYTICS_EVENTS)[number];

export const BUILDER_QUOTE_MODES = ["auto", "template", "manual"] as const;

export type BuilderQuoteMode = (typeof BUILDER_QUOTE_MODES)[number];

export type BuilderAnalyticsMetadata = Readonly<{
  product: "builder";
  mode: BuilderQuoteMode;
}>;

declare global {
  interface Window {
    umami?: {
      track(
        event: BuilderAnalyticsEvent,
        data: BuilderAnalyticsMetadata,
      ): void | PromiseLike<void>;
    };
  }
}

export function isBuilderQuoteMode(value: unknown): value is BuilderQuoteMode {
  return (
    value === "auto" ||
    value === "template" ||
    value === "manual"
  );
}

/**
 * Converts persisted quote-type values to the small analytics enum. Unknown
 * values intentionally return null rather than being sent as free-form data.
 */
export function getBuilderQuoteMode(value: unknown): BuilderQuoteMode | null {
  return isBuilderQuoteMode(value) ? value : null;
}

function isBuilderAnalyticsEvent(value: unknown): value is BuilderAnalyticsEvent {
  return (
    value === "quote_created" ||
    value === "quote_sent" ||
    value === "quote_exported"
  );
}

function isBuilderAnalyticsMetadata(
  value: unknown,
): value is BuilderAnalyticsMetadata {
  if (!value || typeof value !== "object") return false;
  const metadata = value as Record<string, unknown>;
  return (
    metadata.product === "builder" &&
    isBuilderQuoteMode(metadata.mode)
  );
}

/**
 * Send one allow-listed builder event to the injected Umami tracker.
 *
 * The runtime checks are intentional: besides protecting callers written in
 * JavaScript, they ensure an accidental ID, amount, or free-text property can
 * never be forwarded to analytics. Promise rejections are handled as well as
 * synchronous tracker errors.
 */
export function trackBuilderEvent(
  event: BuilderAnalyticsEvent,
  metadata: BuilderAnalyticsMetadata,
): void {
  if (!isBuilderAnalyticsEvent(event) || !isBuilderAnalyticsMetadata(metadata)) {
    return;
  }
  if (typeof window === "undefined") return;

  const safeMetadata: BuilderAnalyticsMetadata = {
    product: "builder",
    mode: metadata.mode,
  };

  try {
    const result = window.umami?.track(event, safeMetadata);
    if (result && typeof result.then === "function") {
      void Promise.resolve(result).catch(() => {});
    }
  } catch {
    // Analytics must never break quote creation, sending, or export.
  }
}

// Keep the generic name available for app code while retaining a descriptive
// name at call sites that are specifically instrumenting builder outcomes.
export const trackEvent = trackBuilderEvent;
export const trackUmamiEvent = trackBuilderEvent;